import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthOptional } from "@/contexts/AuthContext";
import { SectionCard } from "@/components/common/SectionCard";
import { apiFetch } from "./devPanelShared";
import type { DevActionableNote } from "./devActionableNoteTypes";
import { noteExportsToSync } from "./devActionableNoteTypes";
import { devNotesClose } from "@/lib/devActionableNotesClient";
import { DevPanelActionableNotesReview } from "./DevPanelActionableNotesReview";
import { DevPanelActionableNotesPendingEdit } from "./DevPanelActionableNotesPendingEdit";
import { DevPanelActionableNotesReadyEdit } from "./DevPanelActionableNotesReadyEdit";
import {
  DevPanelActionableNoteBodyPreview,
  DevPanelActionableNoteHeader,
} from "./DevPanelActionableNoteCard";
import { DevPanelActionableNoteMetaWithAssetControls } from "./DevPanelActionableNoteAssetSyncControls";

function normalizeNote(raw: DevActionableNote): DevActionableNote {
  return {
    ...raw,
    trust_tier: raw.trust_tier ?? "owner_direct",
    review_status: raw.review_status ?? null,
    effective_body: raw.effective_body ?? null,
  };
}

export function DevPanelActionableNotesSection(props: { getToken: () => Promise<string | null> }) {
  const { getToken } = props;
  const auth = useAuthOptional();
  const isOwner = auth?.status === "ready" && auth.hasRole("platform_owner");
  const userId = auth?.session?.user?.id ?? "";

  const [notes, setNotes] = useState<DevActionableNote[]>([]);
  const [loading, setLoading] = useState(false);
  // Tracks whether at least one successful fetch has resolved. The empty-state
  // message is gated on this so it can't flash during the brief load races
  // before notes have ever been populated (dev-sync note 52448a83).
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [answerDraft, setAnswerDraft] = useState<Record<string, string>>({});
  const [savingId, setSavingId] = useState<string | null>(null);
  const [closingId, setClosingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    const token = await getToken();
    if (!token) return;
    setLoading(true);
    setError(null);
    const result = await apiFetch<{ notes: DevActionableNote[] }>("/api/dev-notes/list", "POST", token);
    setLoading(false);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    setNotes((result.data.notes ?? []).map(normalizeNote));
    setHasLoaded(true);
  }, [getToken]);

  useEffect(() => {
    void load();
  }, [load]);

  async function submitAnswer(note: DevActionableNote) {
    const token = await getToken();
    const answer = (answerDraft[note.id] ?? "").trim();
    if (!token || !answer) return;
    setSavingId(note.id);
    const result = await apiFetch("/api/dev-notes/answer", "POST", token, { id: note.id, answer });
    setSavingId(null);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    setAnswerDraft((prev) => {
      const next = { ...prev };
      delete next[note.id];
      return next;
    });
    void load();
  }

  async function closeNote(note: DevActionableNote) {
    const token = await getToken();
    if (!token) return;
    const consequence = note.bug_report_id
      ? "It will be deleted from the queue and the linked bug report will be marked resolved."
      : "It will be deleted from the queue.";
    if (!window.confirm(`Close this note? ${consequence}`)) return;
    // Only prompt for a resolution ref when there is a linked bug_report —
    // that's the only place it actually lands (bug_reports.resolution_summary).
    let resolution_ref: string | undefined;
    if (note.bug_report_id) {
      const ref = window.prompt(
        "Optional: paste a commit SHA, PR URL, or short note (saved on the resolved bug report). Leave blank to skip.",
        "",
      );
      if (ref === null) return;
      const trimmed = ref.trim();
      if (trimmed) resolution_ref = trimmed.slice(0, 1000);
    }
    setClosingId(note.id);
    const result = await devNotesClose(token, { id: note.id, resolution_ref });
    setClosingId(null);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    void load();
  }

  const pendingReview = useMemo(
    () =>
      notes.filter(
        (n) => n.trust_tier === "needs_review" && n.review_status === "pending" && n.status !== "awaiting_answer",
      ),
    [notes],
  );
  const readyForSync = useMemo(
    () => notes.filter((n) => noteExportsToSync(n) && n.status !== "awaiting_answer"),
    [notes],
  );
  const myPending = useMemo(
    () =>
      notes.filter(
        (n) =>
          n.created_by_user_id === userId &&
          n.trust_tier === "needs_review" &&
          n.review_status === "pending" &&
          n.status !== "awaiting_answer",
      ),
    [notes, userId],
  );
  const awaiting = useMemo(() => notes.filter((n) => n.status === "awaiting_answer"), [notes]);

  const syncReadyCount = readyForSync.filter((n) => n.status === "open" || n.status === "in_progress").length;

  return (
    <SectionCard
      collapsible
      defaultCollapsed={false}
      title="Dev sync queue"
      description={
        <>
          Owner notes sync directly. Dev submissions need owner approve/alter/deny before export. Process on desktop via{" "}
          <code className="rounded bg-gray-100 px-1 text-xs break-all dark:bg-zinc-800">docs/runbooks/dev_sync_and_fix.md</code>.
        </>
      }
      rightSlot={
        <button
          type="button"
          className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200"
          onClick={() => void load()}
          disabled={loading}
        >
          {loading ? "Loading\u2026" : "Refresh"}
        </button>
      }
    >
      {error ? <p className="text-sm text-red-700">{error}</p> : null}

      <p className="text-xs text-gray-600 dark:text-zinc-400">
        {syncReadyCount} ready for sync \u00b7 {pendingReview.length} pending review \u00b7 {awaiting.length} awaiting answer
      </p>

      {hasLoaded && !loading && notes.length === 0 ? (
        <p className="text-sm text-gray-500">No queued notes. Submit a bug with “Queue for dev sync” checked.</p>
      ) : null}

      {isOwner ? (
        <DevPanelActionableNotesReview
          notes={pendingReview}
          getToken={getToken}
          savingId={savingId}
          setSavingId={setSavingId}
          onError={setError}
          onDone={() => void load()}
        />
      ) : null}

      <DevPanelActionableNotesPendingEdit
        notes={myPending}
        getToken={getToken}
        savingId={savingId}
        setSavingId={setSavingId}
        onError={setError}
        onDone={() => void load()}
      />

      {readyForSync.length ? (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-emerald-950">Ready for sync</h3>
          <ul className="space-y-3">
            {readyForSync.map((note) => (
              <li key={note.id} className="rounded-xl border border-emerald-200/80 bg-white p-3 space-y-2 dark:bg-zinc-900">
                <DevPanelActionableNoteHeader note={note} />
                <DevPanelActionableNoteBodyPreview note={note} useEffective />
                <DevPanelActionableNoteMetaWithAssetControls
                  note={note}
                  getToken={getToken}
                  onError={setError}
                  onDone={() => void load()}
                  busy={closingId === note.id}
                  canManageAsset={isOwner || note.created_by_user_id === userId}
                />
                <DevPanelActionableNotesReadyEdit
                  note={note}
                  isOwner={isOwner}
                  canEditBody={
                    note.trust_tier === "owner_direct" && (isOwner || note.created_by_user_id === userId)
                  }
                  getToken={getToken}
                  savingId={savingId}
                  setSavingId={setSavingId}
                  onError={setError}
                  onDone={() => void load()}
                  disabled={closingId === note.id}
                />
                <div className="flex justify-end pt-1">
                  <button
                    type="button"
                    className="rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-900 hover:bg-emerald-100 disabled:opacity-50"
                    disabled={closingId === note.id}
                    onClick={() => void closeNote(note)}
                    title="Mark this note done and remove it from the queue"
                  >
                    {closingId === note.id ? "Closing…" : "Close out"}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {awaiting.length ? (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-amber-950">Awaiting your answer</h3>
          <ul className="space-y-3">
            {awaiting.map((note) => (
              <li key={note.id} className="rounded-xl border border-amber-200/80 bg-white p-3 space-y-2">
                <DevPanelActionableNoteHeader note={note} />
                <DevPanelActionableNoteBodyPreview note={note} />
                {note.clarifying_questions ? (
                  <div className="rounded-lg border border-amber-200 bg-amber-50/80 p-2 space-y-2">
                    <p className="text-xs font-semibold text-amber-950">Clarifying questions (from sync agent)</p>
                    <p className="text-xs text-amber-900 whitespace-pre-wrap">{note.clarifying_questions}</p>
                    <textarea
                      className="w-full rounded border border-amber-200 px-2 py-1.5 text-sm"
                      rows={3}
                      placeholder="Your answers…"
                      value={answerDraft[note.id] ?? ""}
                      onChange={(e) => setAnswerDraft((prev) => ({ ...prev, [note.id]: e.target.value }))}
                    />
                    <button
                      type="button"
                      className="rounded-lg bg-amber-700 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
                      disabled={savingId === note.id || !(answerDraft[note.id] ?? "").trim()}
                      onClick={() => void submitAnswer(note)}
                    >
                      {savingId === note.id ? "Saving…" : "Submit answer & re-queue"}
                    </button>
                  </div>
                ) : null}
                <div className="flex justify-end pt-1">
                  <button
                    type="button"
                    className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-900 hover:bg-amber-100 disabled:opacity-50"
                    disabled={closingId === note.id}
                    onClick={() => void closeNote(note)}
                    title="Abandon this clarify cycle and remove the note from the queue"
                  >
                    {closingId === note.id ? "Closing…" : "Close out"}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </SectionCard>
  );
}
